import { useEffect, useState } from 'react';
import { useContent } from '@/context/ContentContext';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';

export default function Nav() {
  const { t, lang, switchLang } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Prevent body scroll while mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!t) return null;
  const items = [
    { key: 'services', href: '#services' },
    { key: 'process', href: '#process' },
    { key: 'pricing', href: '#pricing' },
    { key: 'faq', href: '#faq' },
    { key: 'contact', href: '#contact' },
  ];

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border] duration-500 ${
        scrolled || open
          ? 'bg-[rgba(5,4,3,0.9)] backdrop-blur-xl border-b border-[var(--line)]'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 h-14 md:h-20 flex items-center justify-between gap-3">
        {/* Brand — smaller on mobile, "Company Ltd" on its own line below */}
        <button
          data-testid="nav-brand"
          onClick={() => { nav('/'); setOpen(false); }}
          className="flex items-center gap-2.5 md:gap-3 group text-left min-w-0"
        >
          <span className="dot flex-shrink-0" />
          <span className="flex flex-col leading-none min-w-0">
            <span className="font-serif tracking-tight text-sm sm:text-base md:text-xl">
              {t.brand}
            </span>
            <span className="text-ink-dim font-mono text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-[0.2em] md:tracking-[0.25em] mt-1">
              Company Ltd
            </span>
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {items.map((it, i) => (
            <a
              key={it.key}
              href={it.href}
              data-testid={`nav-link-${it.key}`}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-dim hover:text-ink link-ember"
            >
              <span className="text-ember mr-1">0{i + 1}</span>
              {t.nav[it.key]}
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <div
            data-testid="nav-lang-toggle"
            className="flex items-center gap-0.5 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.18em] border border-[var(--line)] rounded-full px-1 py-1"
          >
            {['en', 'el'].map((L) => (
              <button
                key={L}
                onClick={() => switchLang(L)}
                data-testid={`lang-${L}`}
                className={`px-2 md:px-2.5 py-1 rounded-full transition-colors ${
                  lang === L ? 'bg-ember text-paper' : 'text-ink-dim hover:text-ink'
                }`}
              >
                {L}
              </button>
            ))}
          </div>
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="hidden md:inline-flex font-mono text-[11px] uppercase tracking-[0.2em] text-ink-dim hover:text-ember link-ember"
          >
            {t.nav.admin}
          </Link>

          {/* Mobile burger */}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-burger"
            aria-label="Toggle menu"
            className="md:hidden inline-flex items-center justify-center w-9 h-9 border border-[var(--line)] rounded-full text-ink"
          >
            {open ? <X className="w-4 h-4" strokeWidth={1.6} /> : <Menu className="w-4 h-4" strokeWidth={1.6} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden border-t border-[var(--line)] bg-[rgba(5,4,3,0.98)] backdrop-blur-xl"
          >
            <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 flex flex-col">
              {items.map((it, i) => (
                <a
                  key={it.key}
                  href={it.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${it.key}`}
                  className="flex items-baseline gap-3 py-4 border-b border-[var(--line)] font-serif text-2xl text-ink hover:text-ember transition-colors"
                >
                  <span className="text-ember font-mono text-[10px] tracking-[0.28em] uppercase w-6 flex-shrink-0">
                    0{i + 1}
                  </span>
                  {t.nav[it.key]}
                </a>
              ))}
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-admin"
                className="flex items-baseline gap-3 py-4 font-serif text-2xl text-ink-dim hover:text-ember transition-colors"
              >
                <span className="text-ember font-mono text-[10px] tracking-[0.28em] uppercase w-6 flex-shrink-0">
                  ↗
                </span>
                {t.nav.admin}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
